import posthog from '../posthog';
import { analyticsState, noteBooked, noteStepReached } from './state';
import { stepIndex } from './steps';

// The only events this widget sends. Nothing is captured automatically — no
// autocapture, no pageviews, no replay — so this list is the whole contract
// with the Consumers project (CIN-7080).
//
// None of these carry consumer personal information: no name, phone, email,
// verification code, comment text, custom-field answer, vehicle detail, or
// customer record id. Counts and ids of things the CUSTOMER configured
// (locations, services) are fine, as is the schedule_id of the appointment
// that was booked; anything the consumer typed or that identifies them is not.
export const EVENTS = {
  WIDGET_OPENED: 'booking_widget_opened',
  STEP_REACHED: 'booking_step_reached',
  STEP_COMPLETED: 'booking_step_completed',
  SUBMITTED: 'booking_submitted',
  COMPLETED: 'booking_completed',
  FAILED: 'booking_failed',
  WIDGET_CLOSED: 'booking_widget_closed',
  NO_AVAILABILITY_SHOWN: 'booking_no_availability_shown',
  WIDGET_LOAD_FAILED: 'booking_widget_load_failed',
};

// Only what the client can actually tell apart. submitBooking collapses a
// network error into status 400, so there is no honest 'network' reason here.
export const BOOKING_FAILURE_REASONS = {
  RATE_LIMITED: 'rate_limited',
  VEHICLE_CONFLICT: 'vehicle_conflict',
  OTHER: 'other',
};

export const LOAD_FAILURE_REASONS = {
  MISSING_CALENDAR: 'missing_calendar',
  LOCATIONS_FAILED: 'locations_failed',
};

const indexOf = (stepId) => stepIndex(stepId, analyticsState());

const capture = (event, properties = {}) => {
  const defined = Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined));
  posthog.capture(event, defined);
};

// Per-step extras that only the step's own screen knows — whether the consumer
// searched the location list, how far they paged the calendar. Buffered here so
// those components report a fact once instead of owning an event, and the
// buffer is drained when the step completes.
let stepDetails = {};

export const recordStepDetail = (stepId, details) => {
  stepDetails[stepId] = { ...stepDetails[stepId], ...details };
};

const drainStepDetails = (stepId) => {
  const details = stepDetails[stepId] || {};
  delete stepDetails[stepId];
  return details;
};

export const resetStepDetails = () => {
  stepDetails = {};
};

export const trackWidgetOpened = ({ landingStep, locationCount }) =>
  capture(EVENTS.WIDGET_OPENED, {
    landing_step: landingStep,
    step_index: indexOf(landingStep),
    is_resumed_session: analyticsState().isResumedSession,
    location_count: locationCount,
  });

export const trackStepReached = ({ stepId, direction }) => {
  noteStepReached(stepId);
  capture(EVENTS.STEP_REACHED, {
    step_id: stepId,
    step_index: indexOf(stepId),
    direction,
  });
};

// Fires for the step being LEFT, on a forward move only, so a consumer stepping
// back and forward again never counts as completing a step twice.
//
// `details` are derived from session state and carry the defaults; the buffered
// extras are reported by the step's own screen and win, so a property is always
// present rather than appearing only for the consumers who triggered it.
export const trackStepCompleted = ({ stepId, ...details }) =>
  capture(EVENTS.STEP_COMPLETED, {
    step_id: stepId,
    step_index: indexOf(stepId),
    ...details,
    ...drainStepDetails(stepId),
  });

export const trackBookingSubmitted = (properties) => capture(EVENTS.SUBMITTED, properties);

export const trackBookingCompleted = (properties) => {
  noteBooked();
  capture(EVENTS.COMPLETED, properties);
};

export const trackBookingFailed = ({ failureReason, httpStatus }) =>
  capture(EVENTS.FAILED, { failure_reason: failureReason, http_status: httpStatus });

// The step is read from the widget's own record rather than passed in: the only
// close choke-point sits above the provider that knows it.
export const trackWidgetClosed = () => {
  const { lastStepId, hasBooked } = analyticsState();
  capture(EVENTS.WIDGET_CLOSED, {
    last_step_id: lastStepId,
    last_step_index: indexOf(lastStepId),
    has_booked: hasBooked,
  });
};

// A dead end: the consumer reached Date & Time and the calendar had nothing to
// offer. Distinct from dropping off, which is a choice.
export const trackNoAvailabilityShown = ({ calendarLocationId, serviceIds }) =>
  capture(EVENTS.NO_AVAILABILITY_SHOWN, {
    calendar_location_id: calendarLocationId,
    service_ids: serviceIds,
    service_count: serviceIds?.length,
  });

export const trackWidgetLoadFailed = ({ failureReason, httpStatus }) =>
  capture(EVENTS.WIDGET_LOAD_FAILED, { failure_reason: failureReason, http_status: httpStatus });
